/**@Note */
/**
 * This provider is based on the root dependencies in
 * apps/front/host-admin/src/App.tsx
 *
 * TODO: Analytics, Datadog, (maybe) Authentication.
 */
import { CssBaseline } from '@mui/material'
import { QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { useTranslation } from 'react-i18next'
import { CacheProvider } from '@emotion/react'
import { clientEmotionCache } from '../../utils/emotion-cache'

import { queryClient } from '@front/api/logic/react-query/queryClient'
import { AppSnackbarProvider } from '@front/components/feedback/snackbar/AppSnackbarProvider'
import { AppLocalizationProvider } from '@front/components/inputs/localization-provider/AppLocalizationProvider'
import { getRuntimeConfig } from '@front/env/config/career'
import { BrandProvider, useRuntimeBrandSwitch } from '@front/theme'

import { appBrandConfig } from '../../branding/appBrandConfig'
import type { ReactNode } from 'react'

/**@ImportantNote */
/**
 * CacheProvider is intentionally absent here (unlike host-admin's root).
 * It is injected higher in the tree by the client/server entries so that
 * the SSR render and client hydration use the same cache instance.
 */

const runtimeConfig = getRuntimeConfig().public

const absoluteUrlOrigin = runtimeConfig.monorepoBaseUrl

export default function HubloProvider({
  children,
}: Readonly<{ children: ReactNode }>) {
  const {
    i18n: { language },
  } = useTranslation()
  const { activeBrandId } = useRuntimeBrandSwitch({
    defaultBrandId: appBrandConfig.brandId,
    enabled: true,
  })

  return (
    <CacheProvider value={clientEmotionCache}>
      <AppLocalizationProvider adapterLocale={language}>
        <BrandProvider
          absoluteUrlOrigin={absoluteUrlOrigin}
          basePath={appBrandConfig.basePath}
          brandId={activeBrandId}
        >
          <AppSnackbarProvider>
            <CssBaseline />
            <QueryClientProvider client={queryClient}>
              {children}
              <ReactQueryDevtools initialIsOpen={false} />
            </QueryClientProvider>
          </AppSnackbarProvider>
        </BrandProvider>
      </AppLocalizationProvider>
    </CacheProvider>
  )
}
